import { useSyncExternalStore } from 'react';

/**
 * PWA "App Mode" helpers.
 *
 * `useIsStandalone()` — true when the app is running as an installed PWA
 * (the chromeless "kiosk" window with no browser address bar / back button),
 * so the shell knows it must supply all navigation itself.
 *
 * `useInstallPrompt()` — exposes the browser's captured `beforeinstallprompt`
 * event so the shell can offer an in-app "Install" button (the equivalent of
 * Chrome's "Install …" affordance). `promptInstall()` shows the native dialog;
 * the button hides once installed or already standalone.
 *
 * The event is captured process-wide by `initInstallPromptCapture()` at
 * startup rather than by a component effect, because the browser fires
 * `beforeinstallprompt` ONCE and early — on the login page, long before the
 * authenticated shell (the only place with an Install button) ever mounts. A
 * component-scoped listener misses it and `canInstall` would stay false for
 * the whole session.
 */

const STANDALONE_QUERY = '(display-mode: standalone)';

function detectStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const byMedia = window.matchMedia?.(STANDALONE_QUERY).matches ?? false;
  // iOS Safari exposes navigator.standalone instead of the display-mode query.
  const byIos = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return byMedia || byIos;
}

function subscribeStandalone(onChange: () => void): () => void {
  const mql = window.matchMedia(STANDALONE_QUERY);
  mql.addEventListener('change', onChange);
  return () => mql.removeEventListener('change', onChange);
}

export function useIsStandalone(): boolean {
  return useSyncExternalStore(subscribeStandalone, detectStandalone, () => false);
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export interface InstallPrompt {
  /** True when the browser has offered an install prompt we can trigger. */
  canInstall: boolean;
  /** Show the native install dialog. No-op if not installable. */
  promptInstall: () => void;
}

// Module-scoped store: the captured event outlives any component, so the
// Install button works whenever the shell happens to mount.
let deferredPrompt: BeforeInstallPromptEvent | null = null;
const subscribers = new Set<() => void>();
let captureStarted = false;

function setDeferredPrompt(next: BeforeInstallPromptEvent | null): void {
  deferredPrompt = next;
  subscribers.forEach((notify) => notify());
}

/**
 * Start listening for `beforeinstallprompt`. Call once at startup (main.tsx),
 * before React renders. Idempotent.
 */
export function initInstallPromptCapture(): void {
  if (captureStarted || typeof window === 'undefined') return;
  captureStarted = true;

  window.addEventListener('beforeinstallprompt', (e) => {
    // Suppress Chrome's mini-infobar so we drive the prompt from our own UI.
    // This does not remove the browser's address-bar / menu install entries.
    e.preventDefault();
    setDeferredPrompt(e as BeforeInstallPromptEvent);
  });
  window.addEventListener('appinstalled', () => setDeferredPrompt(null));
}

function subscribeInstallPrompt(onChange: () => void): () => void {
  subscribers.add(onChange);
  return () => subscribers.delete(onChange);
}

const getInstallPromptSnapshot = () => deferredPrompt;

export function useInstallPrompt(): InstallPrompt {
  const prompt = useSyncExternalStore(
    subscribeInstallPrompt,
    getInstallPromptSnapshot,
    () => null,
  );

  return {
    canInstall: prompt !== null,
    promptInstall: () => {
      if (!prompt) return;
      void prompt.prompt();
      // The event can only be used once — drop it whichever way the user goes.
      void prompt.userChoice.finally(() => setDeferredPrompt(null));
    },
  };
}
